import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useAccountSettingsStore = defineStore(
  'accountSettings',
  () => {
    const accountId = ref('')
    const accountName = ref('')
    const isClientUser = ref(false)
    const sessionToken = ref('')
    const role = ref('')
    const inventoryIds = ref<number[]>([])
    const accountSettings = ref<IAccountSettings | null>(null)

    const setAccountSettings = (settings: IAccountSettings | null) => {
      // Set Account ID
      setAccountName(settings?.accountName ?? '')
      setAccountId(settings?.juid ?? '')

      // Set Inventory IDs
      const inventoryIds = settings?.a5Account ?? []
      const ensuredArrayOfIds = Array.isArray(inventoryIds)
        ? inventoryIds
        : String(inventoryIds)
            .split(',')
            .map((id: string) => id.trim())
      const ensuredNumberIds = ensuredArrayOfIds.map((id: string | number) =>
        Number(id),
      )
      setInventoryId(ensuredNumberIds)

      // Store settings
      accountSettings.value = settings
    }

    const setIsClientUser = (value: boolean) => {
      isClientUser.value = value
    }

    const setSessionToken = (token: string) => {
      sessionToken.value = token
    }

    const normalizedRole = computed(() =>
      String(role.value || '')
        .trim()
        .toLowerCase(),
    )

    // Centralized edit access helper (preferred name)
    const hasEditAccess = computed(() => {
      const r = normalizedRole.value
      return (
        r === 'company_admin' ||
        r === 'company_suadmin' ||
        r === 'company_owner' ||
        r === 'client_admin'
      )
    })

    const hasOwnerAccess = computed(() => {
      const r = normalizedRole.value
      return r === 'company_owner' || r === 'company_suadmin'
    })

    const isClientRole = computed(() =>
      normalizedRole.value.startsWith('client_'),
    )

    const setRole = (userRole: string) => {
      role.value = String(userRole || '')
        .trim()
        .toLowerCase()
      // Keep legacy flag in sync for components still using it
      isClientUser.value = isClientRole.value
    }

    const getAccountSettings = (): any => {
      return accountSettings.value
    }

    const setAccountId = (newAccountId: string) => {
      accountId.value = newAccountId
    }

    const setAccountName = (newAccountName: string) => {
      accountName.value = newAccountName
    }

    const setInventoryId = (newInventoryIds: number[]) => {
      inventoryIds.value = newInventoryIds
    }

    return {
      accountId,
      accountName,
      inventoryIds,
      isClientUser,
      sessionToken,
      role,
      normalizedRole,
      hasEditAccess,
      hasOwnerAccess,
      isClientRole,

      getAccountSettings,
      setAccountSettings,
      setIsClientUser,
      setSessionToken,
      setRole,
    }
  },
  {
    persist: {
      storage: sessionStorage,
      paths: ['accountId'],
    } as any,
  },
)
